import { createApi } from "@reduxjs/toolkit/query/react";
import axios from "axios";
import { Mutex } from "async-mutex";
import { setToken, logout } from "../store/slices/authSlice";
import { env } from "../config/env";

const mutex = new Mutex();

const baseQuery = async ({ url, method, body, params, contentType = "application/json", withCredentials = false, skipAuth = false }, api) => {
  const baseURL = env.apiUrl;
  const isFormData = body instanceof FormData;
  const headers = { Accept: "application/json" };

  if (!isFormData) {
    headers["Content-Type"] = contentType;
  }

  try {
    const token = api.getState().auth.token;

    if (token && !skipAuth) {
      headers.Authorization = `Bearer ${token}`;
    }

    let data = null;
    if (body) {
      if (isFormData) {
        data = body;
      } else if (contentType === "application/json") {
        data = JSON.stringify(nullify(JSON.parse(JSON.stringify(body))));
      } else {
        data = body;
      }
    }

    const result = await axios({
      url: `${baseURL}${url}`,
      method,
      data,
      params,
      headers: {
        ...headers,
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: "0",
      },
      ...(withCredentials && { withCredentials: true }),
    });

    return { data: result.data };
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error(`API Error (${method} ${url}):`, error.response?.data || error.message);
    }

    return {
      error: {
        status: error.response?.status,
        data: error.response?.data || error.message,
      },
    };
  }
};

const nullify = (obj) => {
  if (obj != null && Array.isArray(obj)) {
    for (let i = 0; i < obj.length; i++) {
      if (typeof obj[i] === 'object' && obj[i] !== null && !Array.isArray(obj[i]))
        obj[i] = nullify(obj[i]);
    }
  } else if (obj != null && typeof obj == "object") {
    for (let i = 0, keys = Object.keys(obj); i < keys.length; i++) {
      obj[keys[i]] = nullify(obj[keys[i]]);
    }
  } else if (obj === "" && obj.length === 0 && typeof obj !== "boolean") {
    obj = null;
  }
  return obj;
};

export const baseQueryWithReauth = async (args, api) => {
  await mutex.waitForUnlock();

  let result = await baseQuery(args, api);
  if (result.error && result.error.status === 401) {
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();
      let refreshSuccess = false;
      try {
        const refreshToken = api.getState().auth.refreshToken;
        if (refreshToken) {
          const refreshResult = await baseQuery(
            {
              url: "/auth/token",
              method: "POST",
              body: new URLSearchParams({
                grant_type: "refresh_token",
                refresh_token: refreshToken,
              }).toString(),
              contentType: "application/x-www-form-urlencoded",
              skipAuth: true,
            },
            api
          );

          if (refreshResult.data?.access_token) {
            api.dispatch(
              setToken({
                token: refreshResult.data.access_token,
                refreshToken: refreshResult.data.refresh_token || null,
                expiresAt: refreshResult.data.expires_in
                  ? Date.now() + refreshResult.data.expires_in * 1000
                  : null,
              })
            );
            refreshSuccess = true;
          }
        }
      } catch (e) {
        console.error("Token refresh failed:", e);
      } finally {
        release();
      }

      if (refreshSuccess) {
        result = await baseQuery(args, api);
        if (result.error && result.error.status === 401) {
          api.dispatch(logout());
          window.location.href = "/login";
        }
      } else {
        api.dispatch(logout());
        window.location.href = "/login";
      }
    } else {
      await mutex.waitForUnlock();
      result = await baseQuery(args, api);
      if (result.error && result.error.status === 401) {
        api.dispatch(logout());
        window.location.href = "/login";
      }
    }
  }

  return result;
};

export const baseApi = createApi({
  reducerPath: "api",
  baseQuery: async (args, api) => {
    var processedArgs = {};
    if (args.url === undefined) processedArgs.url = args;
    else processedArgs = args;

    return baseQueryWithReauth(processedArgs, api);
  },
  tagTypes: [
    "User",
    "PricingRules",
  ],
  keepUnusedDataFor: 120,
  refetchOnReconnect: false,
  refetchOnFocus: false,
  endpoints: () => ({})
});

export default baseApi;
